// Header.tsx — Navbar con hamburger + toggle ES/EN + login/logout
import { useState, useEffect, useRef, useCallback, type MouseEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/shared/context/LanguageContext';
import { useTheme } from '@/shared/context/ThemeContext';
import { useAuth } from '@/shared/context/AuthContext';
import { LoginModal } from '@/shared/ui/LoginModal/LoginModal';
import { SECTION_NAV, scrollToSection } from '@/marketing/data/sectionNav';
import { GlobeIcon, RocketIcon, BriefcaseIcon, LightningIcon, SunIcon, MoonIcon } from '@/shared/ui/Icons/Icons';
import styles from './Header.module.css';

export function Header() {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const [isLoginOpen, setIsLoginOpen] = useState<boolean>(false);
  const firstDrawerLinkRef = useRef<HTMLAnchorElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, toggleLang } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const { isAuthenticated, user, logout } = useAuth();
  // El ícono muestra el modo AL QUE SE CAMBIA al hacer click (sol = pasar
  // a claro, luna = pasar a oscuro) — convención estándar de este toggle.
  const themeToggleLabel = theme === 'dark' ? 'Cambiar a modo claro' : 'Cambiar a modo oscuro';
  const ThemeIcon = theme === 'dark' ? SunIcon : MoonIcon;

  const openLogin = useCallback(() => setIsLoginOpen(true), []);
  const closeLogin = useCallback(() => setIsLoginOpen(false), []);
  const handleLoginSuccess = useCallback(() => {
    setIsLoginOpen(false);
    navigate('/dashboard/leads');
  }, [navigate]);

  // El header transparente con texto blanco solo tiene sentido sobre el
  // Hero oscuro de la landing ("/"). En cualquier otra ruta (dashboard,
  // páginas en construcción, 404) el fondo es claro desde arriba, así que
  // forzamos el estilo "scrolled" (texto oscuro) para que no quede
  // texto blanco invisible sobre fondo blanco.
  const isLandingRoute = location.pathname === '/';
  const showSolidHeader = isScrolled || !isLandingRoute;

  useEffect(() => { setIsOpen(false); }, [location.pathname]);
  useEffect(() => {
    const handle = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handle, { passive: true });
    return () => window.removeEventListener('scroll', handle);
  }, []);
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && isOpen) setIsOpen(false); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);
  useEffect(() => {
    if (isOpen) { document.body.classList.add('menu-open'); firstDrawerLinkRef.current?.focus(); }
    else { document.body.classList.remove('menu-open'); }
    return () => document.body.classList.remove('menu-open');
  }, [isOpen]);

  const toggleMenu = useCallback(() => setIsOpen((p) => !p), []);
  const closeMenu = useCallback(() => setIsOpen(false), []);

  // SECTION_NAV es la misma lista que usa el Footer — así ambos muestran
  // siempre las mismas opciones, con el mismo destino.
  const navLinks = SECTION_NAV.map((item) => ({ label: t.nav[item.key], href: item.href }));

  // Scroll centrado en vez del salto por defecto del navegador (que solo
  // alinea el borde superior debajo del header fijo). Se usa tanto para
  // estos links como para el botón CTA, que apunta al mismo tipo de ancla.
  const handleSectionClick = useCallback((e: MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    scrollToSection(href.slice(1));
  }, []);

  return (
    <>
      <header className={`${styles.header} ${showSolidHeader ? styles.scrolled : ''}`} role="banner">
        <Link to="/" className={styles.logo} aria-label="ReInvent-X">
          <LightningIcon aria-hidden="true" /> <span>ReInvent<span className={styles.logoAccent}>-X</span></span>
        </Link>

        <nav className={styles.nav} aria-label="Navegación principal">
          {/* Con sesión iniciada solo importa el idioma y la cuenta —
              el menú de secciones de la landing y el CTA se ocultan. */}
          {!isAuthenticated && navLinks.map((link) => (
            <a key={link.href} href={link.href} className={styles.navLink}
              onClick={(e) => handleSectionClick(e, link.href)}>{link.label}</a>
          ))}

          <button className={styles.langBtn} onClick={toggleLang} aria-label="Cambiar idioma">
            <GlobeIcon aria-hidden="true" /> {t.nav.langLabel}
          </button>

          <button className={styles.themeBtn} onClick={toggleTheme} aria-label={themeToggleLabel} title={themeToggleLabel}>
            <ThemeIcon aria-hidden="true" />
          </button>

          {!isAuthenticated && (
            <a href="#form" className={styles.ctaBtn}
              onClick={(e) => handleSectionClick(e, '#form')}>{t.nav.cta}</a>
          )}

          {isAuthenticated ? (
            <div className={styles.authBox}>
              <Link to="/dashboard/leads" className={styles.authLink}>Hola, {user?.username}</Link>
              <button className={styles.authBtn} onClick={logout}>Cerrar sesión</button>
            </div>
          ) : (
            <button className={styles.authBtn} onClick={openLogin}>Iniciar sesión</button>
          )}
        </nav>

        <div className={styles.headerRight}>
          <button className={styles.langBtnMobile} onClick={toggleLang} aria-label="Cambiar idioma"><GlobeIcon aria-hidden="true" /></button>
          <button className={styles.themeBtnMobile} onClick={toggleTheme} aria-label={themeToggleLabel} title={themeToggleLabel}>
            <ThemeIcon aria-hidden="true" />
          </button>
          <button
            className={`${styles.hamburger} ${isOpen ? styles.open : ''}`}
            onClick={toggleMenu}
            aria-expanded={isOpen}
            aria-controls="mobile-drawer"
            aria-label={isOpen ? 'Cerrar menú' : 'Abrir menú'}
          >
            <span className={styles.bar} />
            <span className={styles.bar} />
            <span className={styles.bar} />
          </button>
        </div>
      </header>

      <nav
        id="mobile-drawer"
        className={`${styles.drawer} ${isOpen ? styles.drawerOpen : ''}`}
        aria-label="Menú móvil"
        aria-hidden={!isOpen}
      >
        {/* Con sesión iniciada se ocultan el menú de secciones y el CTA —
            solo queda el saludo y "Cerrar sesión". */}
        {!isAuthenticated && navLinks.map((link, index) => (
          <a key={link.href} href={link.href} className={styles.drawerLink}
            onClick={(e) => { handleSectionClick(e, link.href); closeMenu(); }}
            ref={index === 0 ? firstDrawerLinkRef : undefined}>{link.label}</a>
        ))}
        {!isAuthenticated && (
          <a href="#form" className={styles.drawerCta}
            onClick={(e) => { handleSectionClick(e, '#form'); closeMenu(); }}><RocketIcon aria-hidden="true" /> {t.nav.cta}</a>
        )}

        {isAuthenticated ? (
          <>
            {/* En responsivo el panel vive aquí, dentro del menú
                hamburguesa — no hay sidebar visible en mobile. */}
            <Link
              to="/dashboard/leads"
              className={styles.drawerLink}
              onClick={closeMenu}
              ref={firstDrawerLinkRef}
            >
              <BriefcaseIcon aria-hidden="true" /> Leads
            </Link>
            <div className={styles.drawerAuthBox}>
              <span className={styles.drawerGreeting}>Hola, {user?.username}</span>
              <button className={styles.drawerAuthBtn} onClick={() => { logout(); closeMenu(); }}>
                Cerrar sesión
              </button>
            </div>
          </>
        ) : (
          <button  className={styles.drawerAuthBtn} onClick={() => { openLogin(); closeMenu(); }}>
            Iniciar sesión
          </button>
        )}
      </nav>

      <LoginModal.Root isOpen={isLoginOpen} onClose={closeLogin}>
        <LoginModal.Header>Iniciar sesión</LoginModal.Header>
        <LoginModal.Body onSuccess={handleLoginSuccess} />
      </LoginModal.Root>
    </>
  );
}

export default Header;
